import { useEffect, useRef, useState } from "react";

import { Colors, Intent, OverlayToaster } from "@blueprintjs/core";
import { useParams } from "react-router";

import {
  isProgramLead,
  isWorkspaceAdmin,
} from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { getExecContact } from "@/components/pages_logged_in/program/components/ProgramDashboard";
import { ProgramDetailsHeader } from "@/components/pages_logged_in/program/components/ProgramDetailsHeader";
import { ProgramDetailsLoader } from "@/components/pages_logged_in/program/components/ProgramDetailsLoader";
import { PROGRAM_TABS_CONFIG } from "@/components/pages_logged_in/program/components/ProgramTabs";
import { InviteProgramParticipantsDialog } from "@/components/pages_logged_in/program/components/program-participants/InviteProgramParticipantsDialog";
import { ProgramParticipantsController } from "@/components/pages_logged_in/program/components/program-participants/ProgramParticipantsController";
import { ProgramParticipantsTable } from "@/components/pages_logged_in/program/components/program-participants/ProgramParticipantsTable";
import { ProgramRightPanel } from "@/components/pages_logged_in/program/components/right-panel/ProgramRightPanel";
import { AddNewUsersCallout } from "@/components/pages_logged_in/workspace-admin/AddNewUsersCallout";
import { BulkInviteScheduleDetailDialog } from "@/components/pages_logged_in/workspace-admin/BulkInviteScheduleDetailDialog";
import { BulkInviteSchedulingBanner } from "@/components/pages_logged_in/workspace-admin/BulkInviteSchedulingBanner";
import { PermissionErrorPage } from "@/components/pages_public/403";
import { NotFound } from "@/components/pages_public/404/NotFound";
import { Container } from "@/components/pieces/bootstrap";
import { CARD_CLASSES, SIDE_NAV_WIDTH } from "@/css/constants";
import {
  JobStatus,
  JobType,
  ProgramComponentsQueryDocument,
  ProgramQueryDocument,
  ProgramStatus,
  ProgramUserRole,
  useProgramQuery,
  useProgramUsersQuery,
  useRemoveProgramParticipantMutation,
  useWorkspaceCreditsQuery,
  useWorkspaceInvitationBatchesForProgramStatusQuery,
  useWorkspaceInvitationBatchesQuery,
  useWorkspaceScheduleJobsQuery,
  WorkspaceInvitationBatchesStatus,
} from "@/graphql";
import {
  ComponentParticipant,
  ParticipantTableField,
  ProgramParticipant as ProgramParticipantType,
} from "@/types/programs";
import { WorkspaceScheduleJob } from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";

const POLLING_INTERVAL_IN_MILLISECONDS = 1500;

type Participant = (ProgramParticipantType | ComponentParticipant) & {
  pendingStatus?: string;
  invitationBatchId?: string;
};

function ProgramParticipant({ programId }: { programId: string }) {
  const [page, setCurrentPage] = useState(1);
  const [size, setSize] = useState(10);
  const [isOpenInvitationDialog, setIsOpenInvitationDialog] = useState(false);
  const [query, setQuery] = useState<string>();
  const toaster = useRef<OverlayToaster>(null);
  const isLead = isProgramLead(programId);

  const [selectedInviteSchedule, setSelectedInviteSchedule] =
    useState<WorkspaceScheduleJob | null>();

  const [orderField, setOrderField] = useState<string>();

  const { data: programData, refetch: refetchProgram } = useProgramQuery({
    variables: {
      programId,
    },
  });

  const {
    data,
    loading,
    refetch: refetchUsers,
  } = useProgramUsersQuery({
    fetchPolicy: "no-cache",
    variables: {
      programId,
      page,
      size,
      orderField: orderField || "",
      roles: [ProgramUserRole.Participant],
      query,
    },
  });

  const program = programData?.program;
  const programUsers = ensureNonNullArray(data?.programUsers?.data);
  const totalRecords = data?.programUsers?.total || 0;
  const isProgramClosed = Boolean(program?.closedAt);
  const isProgramLive = program?.status === ProgramStatus.Live;
  const scheduleText = !isProgramLive
    ? "Invitations go out on launch day. You can fine-tune your participant list up to that point."
    : "";

  const onSort = (field: ParticipantTableField, desc?: boolean) => {
    setCurrentPage(1);
    const orderField = desc ? `-${field}` : field;
    setOrderField(orderField);
  };

  const handleError = (message?: string | null) => {
    if (toaster.current) {
      toaster.current.show({
        message:
          message || "Unable to remove program participant. Please try again.",
        intent: Intent.DANGER,
      });
    }
  };

  const [removeProgramParticipant] = useRemoveProgramParticipantMutation({
    onCompleted: (data) => {
      const { ok, msg } = data?.removeProgramParticipant || {};
      if (ok) {
        refetchUsers();
      } else {
        handleError(msg);
      }
    },
    onError: (err) => {
      handleError(err?.message);
    },
    refetchQueries: [
      {
        query: ProgramComponentsQueryDocument,
        variables: {
          programId,
        },
      },
      {
        query: ProgramQueryDocument,
        variables: {
          programId,
        },
      },
    ],
  });

  const {
    data: invitationBatchesStatusData,
    startPolling: startPollingInvitationBatchesStatus,
    stopPolling: stopPollingInvitationBatchesStatus,
  } = useWorkspaceInvitationBatchesForProgramStatusQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      programId,
    },
    skip: !programId || !isLead,
    onCompleted: (data) => {
      const hasAnyRunningInvitationBatch =
        data?.workspaceInvitationBatchesForProgramStatus ===
        WorkspaceInvitationBatchesStatus.InProgress;

      if (!hasAnyRunningInvitationBatch) {
        stopPollingInvitationBatchesStatus();
        refetchUsers();
      }
    },
    onError: () => {
      stopPollingInvitationBatchesStatus();
      refetchUsers();
    },
  });

  const {
    data: workspaceScheduleJobsData,
    refetch: refetchWorkspaceScheduleJobs,
    previousData: workspaceScheduleJobsPreviousData,
  } = useWorkspaceScheduleJobsQuery({
    variables: {
      jobTypes: [JobType.InviteClientsToWorkspace],
      programId: parseInt(programId),
      statuses: [JobStatus.Scheduled],
    },
    skip: !programId,
    pollInterval: 60000,
  });

  const { data: workspaceCreditsData, refetch: refetchWorkspaceCredits } =
    useWorkspaceCreditsQuery({
      skip: !isWorkspaceAdmin(),
    });

  const { currentWorkspaceCredits } = workspaceCreditsData || {
    currentWorkspaceCredits: "0",
  };

  const workspaceScheduleJobs = ensureNonNullArray(
    workspaceScheduleJobsData?.scheduledJobs,
  );

  useEffect(() => {
    if (workspaceScheduleJobsPreviousData !== workspaceScheduleJobsData) {
      refetchUsers();
    }
  }, [workspaceScheduleJobsPreviousData, workspaceScheduleJobsData]);

  const onRemoveUser = (programUser?: Participant) => {
    if (programUser?.pendingStatus) {
      const jobByBatchId = ensureNonNullArray(workspaceScheduleJobs).find(
        (job) => {
          const { jobPayload } = job || {};
          let jobInfo: ExplicitAny = null;
          try {
            jobInfo = JSON.parse(jobPayload);
          } catch (error) {
            console.error("Error parsing job payload:", error);
            // Ignore parse errors as jobInfo will remain null
          }
          const ret =
            jobInfo?.invitation_batch_id ===
            parseInt(programUser?.invitationBatchId || "0");
          return ret;
        },
      );
      if (jobByBatchId) {
        setSelectedInviteSchedule(jobByBatchId);
      }
    } else if (programUser) {
      removeProgramParticipant({
        variables: {
          programId,
          programUserId: programUser?.id,
        },
      });
    }
  };

  useEffect(() => {
    startPollingInvitationBatchesStatus(POLLING_INTERVAL_IN_MILLISECONDS);
    return () => {
      stopPollingInvitationBatchesStatus();
    };
  }, []);

  const invitationBatchesStatus =
    invitationBatchesStatusData?.workspaceInvitationBatchesForProgramStatus;

  const {
    data: workspaceInvitationBatchesData,
    refetch: refetchWorkspaceInvitationBatches,
  } = useWorkspaceInvitationBatchesQuery({
    variables: {
      programId,
    },
  });

  const hasAnyRunningInvitationBatch =
    invitationBatchesStatus === WorkspaceInvitationBatchesStatus.InProgress;

  const pendingUsers: Participant[] = [];

  workspaceInvitationBatchesData?.workspaceInvitationBatches?.map((item) => {
    const { workspaceInvitations, id: invitationBatchId } = item || {};

    workspaceInvitations?.forEach((invitation) => {
      const { id, email } = invitation || {};

      if (!pendingUsers.find((item) => item.user?.email === email)) {
        pendingUsers.push({
          id: `pending-${id}`,
          roles: [ProgramUserRole.Participant],
          user: {
            email: email || "",
            id: `pending-${id}`,
          },
          pendingStatus: "Invite Pending",
          invitationBatchId: invitationBatchId,
        });
      }
    });
  });

  const hasPendingUsers = pendingUsers.length > 0;

  if (!isLead) return <PermissionErrorPage />;

  return (
    <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
      <Nav />
      {program ? (
        <ProgramDetailsHeader
          program={program}
          initialTab={PROGRAM_TABS_CONFIG[2].key}
          refetchProgram={refetchProgram}
        />
      ) : (
        <ProgramDetailsLoader hasHeader={true} includeSidebar={false} />
      )}

      <Container mb={2}>
        <Flex
          width="100%"
          flexDirection={["column", "column", "column", "row"]}
        >
          <Box flex={1}>
            <Box
              my={36}
              p={24}
              className={CARD_CLASSES}
              css={{ background: Colors.WHITE }}
              flex={1}
              maxWidth={720}
            >
              <ProgramParticipantsController
                query={query || ""}
                onAddParticipants={() => {
                  setIsOpenInvitationDialog(true);
                }}
                totalRecords={totalRecords + pendingUsers.length}
                isLead={isProgramLead(program?.id)}
                isProgramClosed={isProgramClosed}
                onSearch={(value: string) => {
                  setCurrentPage(1);
                  setQuery(value);
                }}
              />
              {hasAnyRunningInvitationBatch && (
                <Box mt={3}>
                  <AddNewUsersCallout
                    status={invitationBatchesStatus}
                    isProgram={true}
                  />
                </Box>
              )}
              {workspaceScheduleJobs.map((job) => (
                <Box mt={3} key={job.id}>
                  <BulkInviteSchedulingBanner
                    onOpenBulkInviteDetail={setSelectedInviteSchedule}
                    scheduleJob={job}
                  />
                </Box>
              ))}
              <Box mt={10}>
                <Flex
                  className="round-corners"
                  padding={"3px 0px"}
                  width="100%"
                  style={{ background: Colors.WHITE }}
                >
                  {program ? (
                    <ProgramParticipantsTable
                      participants={pendingUsers.concat(programUsers)}
                      program={program}
                      onSort={onSort}
                      onRemoveParticipant={(participant) => {
                        onRemoveUser(participant as Participant);
                      }}
                      loading={loading}
                      onAddParticipants={() => {
                        setIsOpenInvitationDialog(true);
                      }}
                      query={query}
                      totalRecords={totalRecords + pendingUsers.length}
                      onPaginationChange={(page: number, size: number) => {
                        setCurrentPage(page);
                        setSize(size);
                      }}
                      pageCount={Math.ceil(
                        (totalRecords + pendingUsers.length) / size,
                      )}
                      hasPendingUsers={hasPendingUsers}
                      onSearch={(value: string) => {
                        setQuery(value);
                        setCurrentPage(1);
                      }}
                    />
                  ) : null}
                </Flex>
              </Box>
            </Box>
          </Box>
          <Box
            width={["100%", "320", 320, 320]}
            ml={[0, 0, 0, 3]}
            px={[3, 0, 0, 0]}
          >
            {program ? (
              <ProgramRightPanel
                program={program}
                isLead={isLead}
                refetchProgram={() => {
                  refetchProgram();
                }}
                quickActions={null}
                showProgramLeads={true}
                showProgramLeadModification={isLead}
                showExecLead={isLead}
                execContact={isLead ? getExecContact(program) : undefined}
              />
            ) : null}
          </Box>
          {program ? (
            <InviteProgramParticipantsDialog
              open={isOpenInvitationDialog}
              afterAddOrUpdateSuccess={() => {
                setIsOpenInvitationDialog(false);
                refetchWorkspaceCredits();
                refetchUsers();
                startPollingInvitationBatchesStatus(
                  POLLING_INTERVAL_IN_MILLISECONDS,
                );
                refetchWorkspaceScheduleJobs();
                refetchWorkspaceInvitationBatches();
              }}
              onClose={() => {
                setIsOpenInvitationDialog(false);
              }}
              program={program}
              currentWorkspaceCredits={currentWorkspaceCredits}
              scheduleText={scheduleText}
            />
          ) : null}

          {selectedInviteSchedule && (
            <BulkInviteScheduleDetailDialog
              inviteSchedule={selectedInviteSchedule}
              workspaceCredit={currentWorkspaceCredits}
              afterAddedMember={() => {
                refetchWorkspaceCredits();
                startPollingInvitationBatchesStatus(
                  POLLING_INTERVAL_IN_MILLISECONDS,
                );
                refetchWorkspaceScheduleJobs();
                refetchWorkspaceInvitationBatches();
              }}
              onClose={() => {
                setSelectedInviteSchedule(undefined);
              }}
              afterModifyInvite={() => {
                refetchWorkspaceScheduleJobs();
                refetchWorkspaceInvitationBatches();
              }}
              allowSchedule={false}
              scheduleText={scheduleText}
              allowSetCredits={isWorkspaceAdmin()}
              allowSetGroup={isWorkspaceAdmin()}
            />
          )}
        </Flex>
      </Container>
    </Box>
  );
}

export function ProgramParticipantPage() {
  const { id } = useParams();

  if (!id) return <NotFound />;

  return (
    <Authenticate maxWidth="100%" layout="full">
      <ProgramParticipant programId={id} />
    </Authenticate>
  );
}
